"use client"

import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

import { Headline } from '@/components/ui/headline'
import { Text } from '@/components/ui/text'
import {buttonVariants} from '@/components/ui/button'
import Link from "next/link";
import CoolingIcon from "@/icons/cooling.svg"
import HeatingIcon from "@/icons/heating.svg"
import VentilationIcon from "@/icons/ventilate.svg"
import {Badge} from "@/components/ui/badge";
import {usePathname, useRouter} from "next/navigation";
import FallbackImage from "@/components/custom/image/fallbackImage";
import {useState} from "react";
import {useSystemSettings} from "@/hooks/useSystemSettings";
import {useTranslations} from "@/contexts/TranslationContext";

const productCardVariants = cva('card border-gray-beige col-span-full flex flex-col overflow-clip rounded-md border bg-gray-beige-light transition duration-200 hover:border-aloevera', {
    variants: {
        variant: {
            vertical: 'sm:col-span-2 lg:col-span-4',
            horizontal: 'grid grid-cols-subgrid items-center'
        }
    }
})

interface ProductCardProps {
    id: string;
    variant?: "vertical" | "horizontal" | null | undefined;
    image: {
        src: string;
        alt?: string;
        width?: number;
        height?: number;
    } | null,
    title: string;
    subtitle:string;
    content?: string[];
    url: string;
    newBadge?: boolean;
    heating?: boolean,
    cooling?: boolean,
    ventilation?: boolean
    imageClass?: string,
    buttonText?: string,
}

/**
 * @param {string} id Component ID.
 * @param {string} variant Optional, component variant. Default = vertical. Options: vertical, horizontal.
 * @param {{
 *         src: string;
 *         alt?: string;
 *         width?: number;
 *         height?: number;
 *     }} image Card image data.
 * @param {string} title Card heading.
 * @param {string} subtitle Card subheading.
 * @param {string[]} content Optional, card body content strings.
 * @param {string} url Unused
 * @param {boolean} newBadge Optional, controls whether a badge is shown in the top right corner. Default = false.
 * @param primaryLink Optional, data for the primary card button.
 * @param secondaryLink Optional, data for the secondary card button.
 * @param {boolean} heating Optional, displays an icon to indicate that the product has heating capability. Default = false.
 * @param {boolean} cooling Optional, displays an icon to indicate that the product has cooling capability. Default = false.
 * @param {boolean} ventilation Optional, displays an icon to indicate that the product has air flow capability. Default = false.
 */
const ProductCard = ({ id, variant = 'vertical', image = null, title, subtitle, content, url, newBadge = false, heating, cooling, ventilation, imageClass, buttonText }: ProductCardProps) => {
    const pathname = usePathname();
    const router = useRouter();
    const { scopeSystem, unitSystem } = useSystemSettings();

    const [hover, setHover] = useState(false);

    const t = useTranslations();

    const imageClasses = `images relative bg-white aspect-video border-b border-b-white ${variant === 'horizontal' ? 'col-span-full my-4 md:col-span-3 lg:my-8 lg:ml-8' : ''}`
    const contentClasses = `content bg-gray-beige-light ${variant === 'horizontal' ? 'col-span-full flex flex-col md:col-span-6  md:col-span-3' : ''}`
    const headerClasses = `header ${variant === 'horizontal' ? 'w-full bg-gray-beige-light' : 'bg-gray-beige-light p-4 lg:p-7'}`
    const teaserClasses = `teaser text-gray-beige-dark ${variant === 'horizontal' ? 'mt-4' : 'p-4 lg:p-7'}`
    const footerClasses = `footer flex flex-col gap-y-3 p-4 lg:p-7 bg-gray-beige-light ${variant === 'horizontal' ? 'col-span-full md:col-span-3' : 'mt-auto !pt-0'}`

    return (
        <Link
            className={cn(productCardVariants({ variant }), `${url ? "cursor-pointer" : ""}`)}
            href={`${pathname}/${url ? url : "#"}?scopeSystem=${scopeSystem}&unitSystem=${unitSystem}`}
            data-product-id={id}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
                <div className={imageClasses}>
                    <FallbackImage image={image} className={`${imageClass} transition-transform transform ${hover ? "scale-105" : ""}`} border={false} />
                    {newBadge && (<Badge style={{position: "absolute", right: "3%", top: "3%"}} variant={"aloevera"}>{t["productCard.newBadge"]}</Badge>)}
                </div>
            <div className={contentClasses}>
                <div className={cn(headerClasses, "flex flex-col justify-center items-center")}>
                    <div className={"flex flex-row gap-x-3"}>
                        <Headline variant='h4' size='lg' className={`font-normal transition-all duration-500 text-center ${ hover ? "underline" : "" }`}>
                            {title}
                        </Headline>
                        <div className={"flex flex-row gap-x-1 self-center"}>
                            {heating && (<HeatingIcon width={24} height={24} color={"red"}/>)}
                            {cooling && (<CoolingIcon width={24} height={24} color={"lightBlue"}/>)}
                            {ventilation && (<VentilationIcon width={24} height={24} color={"lightGreen"}/>)}
                        </div>
                    </div>
                    <Text className='text-gray-beige-dark text-center'>{subtitle}</Text>
                </div>
                {/*<div className={teaserClasses}>*/}
                {/*    /!*{!content && <Text className='text-gray-beige-dark'>Missing Content</Text>}*!/*/}
                {/*    {content && <List variant='arrow' items={content}/>}*/}
                {/*</div>*/}
            </div>
            <div className={footerClasses}>
                {url && (
                    <div className={cn(buttonVariants({ variant: "primary" }), `transition-all duration-500 translate-y-8 ${hover ? "opacity-100 translate-y-0" : " opacity-0 translate-y-8"}`)}>
                        {buttonText}
                    </div>
                )}
            </div>
        </Link>
    )
}

export { ProductCard }